// Bucket-B: PrivateRoute-gated paths with no MENU_ELEMENTS/registry presence at all (drilldowns,
// sub-pages, and app-chrome routes that will never become their own `_dashboard` registry rows).
// Every (view, paths) group and every path below was derived directly from PrivateRoute.tsx's
// current PAGE_VIEW_MAPPING/ALL_PATHS substring-matching behavior (src/constants/menuItems.ts),
// not guessed - each path here is granted TODAY only because it happens to contain one of
// ALL_PATHS's entries as a substring. Exact literal paths (not prefixes) keep the Bucket-A/B split
// airtight: a path can appear here or in DASHBOARD_REGISTRY, never rely on runtime overlap.
export interface ResidualAccessRule {
  internal_view: string;
  paths: string[];
}

export const RESIDUAL_BUCKET_B_RULES: ResidualAccessRule[] = [
  { internal_view: "home", paths: ["/home"] },
  { internal_view: "user_profile", paths: ["/user_profile"] },
  // Real, confirmed via a live divergence log (2026-08-14) that surfaced a genuine account holding
  // "info" but neither of the two guessed names below - cross-referenced directly against
  // cedar-backend (feat-2/cedar-restructure): InternalView (constants/custom_types.py) has no
  // data_info/dashboard_info/release_notes member at all, only `info`, and every real route behind
  // these pages (routes/user_messages.py's get-all-release-notes/get-release-note-by-id/
  // get-latest-release-note, routes/_legacy/available_data.py, routes/_legacy/power_bi.py) is
  // gated on exactly InternalView.info. The three distinct names below were a guess (each just
  // mirrored its own path's name, the same convention used elsewhere in this file) made without
  // that cross-reference at the time - now corrected to the one real shared view.
  { internal_view: "info", paths: ["/data_info", "/dashboard_info", "/release_notes"] },
  { internal_view: "admin", paths: ["/admin"] },
  {
    internal_view: "ccs_vital_signs",
    paths: [
      "/ccs_vital_signs/app_ratings_breakdown",
      "/ccs_vital_signs/data_quality",
      "/ccs_vital_signs/ccs_journey",
      "/ccs_vital_signs/ccs_journey/europe_activation",
      "/ccs_vital_signs/ccs_journey/uae_activation",
      "/ccs_vital_signs/ccs_journey/asean_activation",
      "/ccs_vital_signs/top_flop",
      "/ccs_vital_signs/ccs_analysis",
      "/ccs_vital_signs/ccs_monetization/stv_subscriptions",
    ],
  },
  // "/purchase_funnel"/"diagnostic" are a separate IFRAME feature from "/purchase_funnel_report"
  // (registry POI poi_purchase_funnel_report) - they only share the "purchase_funnel" view today
  // via a substring coincidence in ALL_PATHS, preserved here rather than "fixed" (shadow mode must
  // stay behaviorally frozen against the legacy system).
  { internal_view: "purchase_funnel", paths: ["/purchase_funnel", "/purchase_funnel/diagnostic", "/purchase_funnel_report/msrp"] },
  { internal_view: "product_marketing_optimization", paths: ["/product_marketing_optimization/dfp_dashboard"] },
  { internal_view: "mobility_report", paths: ["/mobility_report/insights"] },
  { internal_view: "digital_customer_experience", paths: ["/digital_user_experience_core_models"] },
  {
    internal_view: "ariya_experience_monitor",
    paths: [
      "/ariya/experience",
      "/ariya/handraisers/market-split",
      "/ariya/handraisers",
      "/ariya/preorder/japan",
      "/ariya/preorder/norway",
      "/ariya/preorder/united-states",
      "/ariya/preorder/united-kingdom",
      "/ariya/preorder/netherlands",
      "/ariya/preorder/canada",
      "/ariya/preorder/france",
      "/ariya/preorder/germany",
      "/ariya/preorder/italy",
      "/ariya/preorder/sweden",
      "/ariya/preorder/belgium",
      "/ariya/preorder",
    ],
  },
  {
    internal_view: "midas",
    paths: [
      "/midas/fmi_by_model",
      "/midas/media_mix",
      "/midas/mtp_category",
      "/midas/fixed",
      "/midas/strategic_ce",
      "/midas/strategic_dealer_support",
      "/midas/spend_vs_traffic",
    ],
  },
  { internal_view: "voc", paths: ["/voc/voc_rsa", "/voc/google_rating", "/voc/some"] },
  { internal_view: "journey_excellence", paths: ["/journey_excellence/deep_dive"] },
  { internal_view: "dmeo", paths: ["/dmeo/landing-page-performance"] },
  {
    internal_view: "lead_management_report",
    paths: ["/lead_management_report/high_level", "/lead_management_report/detail_level_one", "/lead_management_report/detail_level_two"],
  },
  // These are top-level dashboards, not drilldowns - an exception to this file's own header
  // comment, added deliberately. A 2026-08-12 real GET /dashboards response (46 rows) showed no
  // row for any of product_performance/car_configurator_g5/the 7 streamlit tools below, and per
  // product confirmation at the time that was believed to be the real final seed state. A newer
  // real response since then (2026-08-14, 41 rows) directly contradicts that: abt_data_query,
  // correspondence_mapping, pricing_project, verbatim_analysis, visit_to_lead, serious_buyer, and
  // configurator_g5 all now have real `_dashboard` rows (ids 34-40) - per product decision, those
  // 7 moved back to DASHBOARD_REGISTRY (see that file's own comment). Only `clv` and
  // `product_performance` are still genuinely absent from every real response seen so far, so
  // only those two stay here. Each keeps the exact view PAGE_VIEW_MAPPING already grants it
  // today, so this remains a pure like-for-like carry-over, not a new access decision.
  { internal_view: "product_performance", paths: ["/product_performance"] },
  // new_beta_car_configurator is still absent from every real response seen so far (unlike
  // configurator_g5, now moved to DASHBOARD_REGISTRY) - stays here until it gets a real row.
  { internal_view: "car_configurator_jp", paths: ["/beta/car_configurator"] },
  { internal_view: "streamlit", paths: ["/streamlit/clv"] },
];

// Pages re-gated from fully-public back to PrivateRoute (see App.tsx's former "Guards
// intentionally removed for now" comment) - general shell pages any authenticated user should
// reach, not tied to a specific dashboard view. Checked before, and never part of, the
// old/new shadow comparison (see accessControl.ts).
export const PUBLIC_AUTHENTICATED_PATHS: string[] = [
  "/favourites",
  "/dashboards",
  "/analytics",
  "/settings",
  "/faq",
  "/alerts",
  "/archived",
  // /cedar_ai temporarily removed here - see cedarShellNavConfig.ts's own comment.
  // Formerly /global_ms_sales/pdf_page, gated behind the "global_ms_sales" dashboard view even
  // though it's linked from several unrelated dashboards (SeoFilters.tsx, TrimMixReport.tsx) - a
  // generic "link your Google account" utility, not tied to one dashboard, so it belongs here
  // like the other shell pages above rather than in DASHBOARD_REGISTRY.
  "/faq/link_google_account",
];
